import {
  SET_ACTION_STATUS_PENDING,
  SET_ACTION_STATUS_SUCCESS,
  SET_ACTION_STATUS_ERROR,
} from '@/store/helpers';
import {
  SET_NUMBER_AVAILABLE_COUNTRIES,
  SET_USER_VISIBLE_COLUMNS, 
  SET_PENDING_ORDERS,
  SET_ORDERS,
  SET_SELECTED_ORDER,
  SET_SELECTED_ORDER_ID,
  SET_NUMBER_POOLS,
  SET_DAY_STATS,
  SET_MONTH_STATS,
  SET_NUMBER_POOL_REFERENCES,
  SET_ACCOUNT_SETTINGS,
  SET_WIDGET_TITLE as SET_WIDGET_TITLE_MUTATION,
} from './mutationTypes';
import {
  GET_NUMBER_AVAILABLE_COUNTRIES,
  GET_NUMBER_POOLS,
  GET_NUMBER_POOL,
  GET_NUMBER_ORDERS,
  GET_NUMBER_ORDER,
  GET_PENDING_NUMBER_ORDERS,
  CANCEL_NUMBER_ORDER,
  SELECT_NUMBER_ORDER,
  UNSELECT_NUMBER_ORDER,
  CREATE_NUMBER,
  EDIT_NUMBER,
  CREATE_NUMBER_ORDER,
  ATTACH_PUBLISHER_TO_NUMBER,
  CREATE_NUMBER_POOL,
  EDIT_NUMBER_POOL,
  GROW_NUMBER_POOL,
  SHRINK_NUMBER_POOL,
  GET_USER_VISIBLE_COLUMNS,
  GET_NUMBER_POOL_REFERENCES,
  DELETE_NUMBER_POOL,
  GET_ACCOUNT_SETTINGS,
  SET_WIDGET_TITLE,
} from './actionTypes';
import { filter, map } from 'lodash';


import { numberService, numberPoolService } from '@/angularBridgeCode/ringbaPortal.servicesModule';

const actions = {

  async [SET_WIDGET_TITLE]({ commit }, { title }) {
    commit(SET_WIDGET_TITLE_MUTATION, title);
  },

  async [GET_ACCOUNT_SETTINGS]({ rootState, commit }) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: GET_ACCOUNT_SETTINGS });
    try {
      const response = await accountSettingsService.loadSettings(rootState.currentAccountId);
      const accountSettings = response.data.accountSettings;
      commit(SET_ACCOUNT_SETTINGS, accountSettings);
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: GET_ACCOUNT_SETTINGS });
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: GET_ACCOUNT_SETTINGS, error: e });
    }
  },
  async [GET_NUMBER_POOLS]({ rootState: { currentAccountId }, commit }) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: GET_NUMBER_POOLS });

    try {
      const [numberPoolsRes, dayStatsRes, monthStatsRes] = await Promise.all([
        numberPoolService.getNumberPools(currentAccountId),
        numberPoolService.getNumberPoolsDayStats(currentAccountId),
        numberPoolService.getNumberPoolsMonthStats(currentAccountId)
      ]);

      const numberPools = numberPoolsRes.data?.numberPools || [];
      const dayStats = dayStatsRes.data?.values || [];
      const monthStats = monthStatsRes.data?.values || [];

      commit(SET_NUMBER_POOLS, numberPools);
      commit(SET_DAY_STATS, dayStats);
      commit(SET_MONTH_STATS, monthStats);
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: GET_NUMBER_POOLS });

    } catch (error) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: GET_NUMBER_POOLS, error });
    }
  },
  async [GET_NUMBER_POOL]({ rootState, commit, state }, { numberPoolId }) {
    commit('SET_ACTION_STATUS_PENDING', { actionName: 'GET_NUMBER_POOL' });

    try {
      const [response, dayStatsRes, monthStatsRes] = await Promise.all([
        numberPoolService.getNumberPool(rootState.currentAccountId, numberPoolId),
        numberPoolService.getNumberPoolsDayStats(rootState.currentAccountId),
        numberPoolService.getNumberPoolsMonthStats(rootState.currentAccountId)
      ]);

      const updatedNumberPool = response.data.numberPool;
      const dayStats = dayStatsRes.data?.values || [];
      const monthStats = monthStatsRes.data?.values || [];

      const numberPools = state.numberPools.map(pool =>
        pool.id === numberPoolId ? updatedNumberPool : pool
      );

      if (!numberPools.find(pool => pool.id === numberPoolId)) {
        numberPools.push(updatedNumberPool);
      }

      commit(SET_NUMBER_POOLS, numberPools);
      commit(SET_DAY_STATS, dayStats);
      commit(SET_MONTH_STATS, monthStats);
      commit(SET_WIDGET_TITLE_MUTATION, `Number Pool: ${updatedNumberPool.name}`);
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: 'GET_NUMBER_POOL' });
    } catch (error) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: 'GET_NUMBER_POOL', error });
    }
  },
  async [GET_NUMBER_ORDER]({ rootState, commit }, orderId) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: GET_NUMBER_ORDER });
    try {
      const response = await numberService.getNumberOrder(rootState.currentAccountId, orderId);
      const selectedOrder = response.data.scheduledPurchase;
      selectedOrder.publisher = rootState.users.publishers[selectedOrder.affiliateId];
      commit(SET_SELECTED_ORDER, selectedOrder);
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: GET_NUMBER_ORDER });
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: GET_NUMBER_ORDER, error: e });
    }
  },

  async [SELECT_NUMBER_ORDER]({ commit, dispatch }, orderId) {
    commit(SET_SELECTED_ORDER_ID, orderId);
    dispatch(GET_NUMBER_ORDER, orderId);
  },

  async [UNSELECT_NUMBER_ORDER]({ commit }) {
    commit(SET_SELECTED_ORDER_ID, '');
    commit(SET_SELECTED_ORDER, {});
  },

  async [GET_NUMBER_ORDERS]({ rootState, commit }, rangeFilter) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: GET_NUMBER_ORDERS });
    try {
      const response = await numberService.getNumberOrders(rootState.currentAccountId, rangeFilter);
      commit(SET_ORDERS, response.data.allocationJobs);
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: GET_NUMBER_ORDERS });
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: GET_NUMBER_ORDERS, error: e });
    }
  },

  async [GET_PENDING_NUMBER_ORDERS]({ rootState, commit }) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: GET_PENDING_NUMBER_ORDERS });
    try {
      const response = await numberService.getPendingNumberOrders(rootState.currentAccountId);
      commit(SET_PENDING_ORDERS, response.data.allocationJobs);
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: GET_PENDING_NUMBER_ORDERS });
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: GET_PENDING_NUMBER_ORDERS, error: e });
    }
  },

  async [CANCEL_NUMBER_ORDER]({ rootState, commit, dispatch }, orderId) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: CANCEL_NUMBER_ORDER });
    try {
      await numberService.cancelNumberOrder(rootState.currentAccountId, orderId);
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: CANCEL_NUMBER_ORDER });
      dispatch(GET_NUMBER_ORDERS);
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: CANCEL_NUMBER_ORDER, error: e });
    }
  },

  async [CREATE_NUMBER]({ commit }, { accountId, assignmentSettings }) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: CREATE_NUMBER });
    try {
      const data = await numberService.createNumber(accountId, assignmentSettings);
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: CREATE_NUMBER });
      return data;
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: CREATE_NUMBER, error: e });
    }
  },

  async [ATTACH_PUBLISHER_TO_NUMBER]({ commit }, { accountId, numberId, publisherId }) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: ATTACH_PUBLISHER_TO_NUMBER });
    try {
      await numberService.attachAffiliate(accountId, numberId, publisherId);
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: ATTACH_PUBLISHER_TO_NUMBER });
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: ATTACH_PUBLISHER_TO_NUMBER, error: e });
    }
  },

  async [CREATE_NUMBER_ORDER]({ commit }, { accountId, assignmentSettings }) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: CREATE_NUMBER_ORDER });
    try {
      const data = await numberService.createNumberOrder(accountId, assignmentSettings);
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: CREATE_NUMBER_ORDER });
      return data;
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: CREATE_NUMBER_ORDER, error: e });
    }
  },

  async [EDIT_NUMBER]({ commit }, { accountId, currentNumber }) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: EDIT_NUMBER });
    try {
      const data = await numberService.updateNumber(accountId, currentNumber.id, currentNumber);
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: EDIT_NUMBER });
      return data;
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: EDIT_NUMBER, error: e });
    }
  },

  async [CREATE_NUMBER_POOL]({ rootState, commit }, formData) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: CREATE_NUMBER_POOL });
    try {
      const response = await numberPoolService.createNumberPool(rootState.currentAccountId, formData);
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: CREATE_NUMBER_POOL });
      return response.data;
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: CREATE_NUMBER_POOL, error: e });
    }
  },

  async [EDIT_NUMBER_POOL]({ rootState, commit }, { numberPoolId, numberPool }) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: EDIT_NUMBER_POOL });
    try {
      const response = await numberPoolService.updateNumberPool(rootState.currentAccountId, numberPoolId, numberPool);
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: EDIT_NUMBER_POOL });
      return response.data;
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: EDIT_NUMBER_POOL, error: e });
    }
  },

  async [GROW_NUMBER_POOL](
    { rootState, commit },
    { numberPoolId, numbersAllocating, chargeAccountIfNeeded }
  ) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: GROW_NUMBER_POOL });
    try {
      await numberPoolService.allocateMoreNumbers(
        rootState.currentAccountId,
        numberPoolId,
        numbersAllocating,
        chargeAccountIfNeeded
      );
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: GROW_NUMBER_POOL });
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: GROW_NUMBER_POOL, error: e });
    }
  },

  async [SHRINK_NUMBER_POOL](
      { rootState, commit },
      { numberPoolId, count, keepNumbers }
    ) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: SHRINK_NUMBER_POOL });
    try {
      await numberPoolService.shrinkPool(rootState.currentAccountId, numberPoolId, {
        count,
        keepNumbers,
      });
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: SHRINK_NUMBER_POOL });
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: SHRINK_NUMBER_POOL, error: e });
    }
  },

  async [GET_USER_VISIBLE_COLUMNS]({ commit }) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: GET_USER_VISIBLE_COLUMNS });
    commit(SET_USER_VISIBLE_COLUMNS, []);

    try {
      var userVisibleColumns = map(filter(numberPoolService.columnSettings, (col) => !col.hide), (col) => {
        return col.name || col.displayName;
      });
      commit(SET_USER_VISIBLE_COLUMNS, userVisibleColumns);
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: GET_USER_VISIBLE_COLUMNS });
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: GET_USER_VISIBLE_COLUMNS, error: e });
    }
  },

  async [GET_NUMBER_POOL_REFERENCES]({ rootState, commit }, { numberPoolId }) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: GET_NUMBER_POOL_REFERENCES });
    commit(SET_NUMBER_POOL_REFERENCES, null);
  
    try {
      const response = await numberPoolService.getNumberPoolReferences(
        rootState.currentAccountId,
        numberPoolId
      );
      commit(SET_NUMBER_POOL_REFERENCES, response.data);
      commit(SET_ACTION_STATUS_SUCCESS, {
        actionName: GET_NUMBER_POOL_REFERENCES,
      });
    } catch (error) {
      commit(SET_ACTION_STATUS_ERROR, {
        actionName: GET_NUMBER_POOL_REFERENCES,
        error,
      });
    }
  },

  async [DELETE_NUMBER_POOL]({ rootState, commit }, { numberPoolId, force = false, keepNumbers = false }) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: DELETE_NUMBER_POOL });
  
    try {
      await numberPoolService.deleteNumberPool(
        rootState.currentAccountId,
        numberPoolId,
        force,
        keepNumbers
      );
  
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: DELETE_NUMBER_POOL });
    } catch (error) {
 
      commit(SET_ACTION_STATUS_ERROR, { actionName: DELETE_NUMBER_POOL, error });
      console.error('Error deleting number pool:', error);
    }
  },

  async [GET_NUMBER_AVAILABLE_COUNTRIES]({ rootState, commit }) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: GET_NUMBER_AVAILABLE_COUNTRIES });
    try {
      const response = await numberService.getAvailableCountries(rootState.currentAccountId);
      commit(SET_NUMBER_AVAILABLE_COUNTRIES, response.data);
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: GET_NUMBER_AVAILABLE_COUNTRIES });
      return data;
    } catch (e) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: GET_NUMBER_AVAILABLE_COUNTRIES, error: e });
    }
  },

};

export default actions;
